import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowLeft, ExternalLink, Loader2, Printer } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  FieldTripReleaseStep,
  MediaReleaseStep,
  PaymentStep,
  PolicyAcknowledgmentsStep,
} from '~/pages/ParentPortal/packet-steps';
import type { AppLayoutContext } from '~/types';
import { useEnrollmentPacketForAdmin } from './useEnrollmentPacketForAdmin';

export type SignedPacketMode = 'packet' | 'agreement';

interface PacketStudent {
  studentEnrollmentId: string;
  firstName: string;
  lastName: string;
  gradeLevel?: string;
}

const PRINT_CLASS = 'printing-signed-packet';

const noop = () => {};

// Blocks clicks and focus without the greyed-out look `disabled` adds, so each step
// renders the way the family saw it. React 18's types don't know `inert` yet.
const inertProps = { inert: '' } as Record<string, string>;

const POLICY_DOCUMENT_LABELS: Array<[string, string]> = [
  ['tuitionFeeScheduleUrl', 'Tuition & Fee Schedule'],
  ['handbookUrl', 'Handbook'],
  ['uniformPolicyUrl', 'Uniform Policy'],
];

function StepSection({ children }: { children: ReactNode }) {
  return <section className="rounded-lg border bg-card p-6">{children}</section>;
}

function Detail({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

// The app layout scrolls inside a fixed-height container, so printing it in place
// yields one clipped page with the sidebar. While printing (the Print button or the
// browser's own print command), the document moves to a body-level portal that
// app.css prints alone.
function usePrintMode() {
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    const enter = () => {
      flushSync(() => setPrinting(true));
      document.body.classList.add(PRINT_CLASS);
    };
    const leave = () => {
      document.body.classList.remove(PRINT_CLASS);
      setPrinting(false);
    };
    window.addEventListener('beforeprint', enter);
    window.addEventListener('afterprint', leave);
    return () => {
      window.removeEventListener('beforeprint', enter);
      window.removeEventListener('afterprint', leave);
      document.body.classList.remove(PRINT_CLASS);
    };
  }, []);

  return printing;
}

export default function SignedEnrollmentPacket({ mode }: { mode: SignedPacketMode }) {
  const { familySlug, familyEnrollmentId } = useParams();
  const { school } = useOutletContext<AppLayoutContext>();
  const { data, loading, error } = useEnrollmentPacketForAdmin(familyEnrollmentId);
  const printing = usePrintMode();
  const packet = data?.enrollmentPacketForAdmin;

  const familyPath = `/app/${school.slug}/families/${familySlug}`;
  const encodedId = encodeURIComponent(familyEnrollmentId ?? '');
  const otherView = mode === 'agreement'
    ? { to: `${familyPath}/enrollment-packet/${encodedId}`, label: 'Full signed packet' }
    : { to: `${familyPath}/tuition-agreement/${encodedId}`, label: 'Tuition agreement only' };

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`${familyPath}/enrollment`}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to enrollment
        </Link>
      </Button>
      {packet && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={otherView.to}>{otherView.label}</Link>
          </Button>
          <Button size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        {toolbar}
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !packet) {
    return (
      <div className="p-6 space-y-6">
        {toolbar}
        <div className="flex items-center gap-2 text-muted-foreground">
          <AlertCircle className="h-5 w-5" />
          <span>{error ? 'Failed to load the signed packet.' : 'No signed enrollment packet found for this enrollment.'}</span>
        </div>
      </div>
    );
  }

  const answers = (packet.formAnswers ?? {}) as Record<string, any>;
  const students = (packet.students ?? []) as PacketStudent[];
  const releases = (answers.studentReleases ?? {}) as Record<string, any>;
  const policyDocuments = (packet.policyDocuments ?? {}) as Record<string, string>;
  const title = mode === 'agreement' ? 'Tuition Agreement' : 'Signed Enrollment Packet';

  const packetDocument = (
    <div className="space-y-6">
      <header className="space-y-3 border-b pb-4">
        <div>
          <p className="text-sm text-muted-foreground">{packet.schoolName} · {packet.schoolYearName}</p>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-lg">{packet.familyName}</p>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Detail label="Signed by" value={packet.signedBy} />
          <Detail label="Signed at" value={new Date(packet.signedAt as string).toLocaleString()} />
          <Detail label="IP address" value={packet.signedIpAddress} />
          <Detail label="Payment plan" value={packet.paymentOptionName} />
        </dl>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {POLICY_DOCUMENT_LABELS.filter(([key]) => policyDocuments[key]).map(([key, label]) => (
            <a
              key={key}
              href={policyDocuments[key]}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {label}
              <ExternalLink className="h-3 w-3 print:hidden" />
            </a>
          ))}
        </div>
      </header>

      {packet.invoicesChangedSinceSigning && (
        <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 print:hidden">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p>
            This family's invoices have changed since they signed. The amounts below are what the packet
            showed them at signing; the Billing tab has their current invoices.
          </p>
        </div>
      )}

      <p className="text-sm text-muted-foreground print:hidden">
        Each step is shown as the family saw it, with their answers filled in.
      </p>

      <div {...inertProps} className="space-y-6">
        {mode === 'packet' && students.map((student) => {
          const studentReleases = releases[student.studentEnrollmentId] ?? {};
          return (
            <Fragment key={student.studentEnrollmentId}>
              <StepSection>
                <MediaReleaseStep
                  student={student}
                  release={studentReleases.mediaRelease ?? {}}
                  onReleaseChange={noop}
                />
              </StepSection>
              <StepSection>
                <FieldTripReleaseStep
                  student={student}
                  release={studentReleases.fieldTripRelease ?? {}}
                  onReleaseChange={noop}
                />
              </StepSection>
            </Fragment>
          );
        })}

        <StepSection>
          <PolicyAcknowledgmentsStep
            policyDocuments={policyDocuments}
            invoiceSummary={packet.invoiceSummary}
            acknowledgments={answers.policyAcknowledgments ?? {}}
            onAcknowledgmentChange={noop}
          />
        </StepSection>

        <StepSection>
          <PaymentStep
            invoiceSummary={packet.invoiceSummary}
            paymentMethod={answers.paymentMethod}
            onPaymentMethodChange={noop}
            signature={answers.signature}
            onSignatureChange={noop}
            paymentSettings={packet.paymentSettings}
          />
        </StepSection>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {toolbar}
      {printing
        ? createPortal(<div className="signed-packet-print">{packetDocument}</div>, document.body)
        : packetDocument}
    </div>
  );
}
